import 'project/polyfill'
import classNames from 'classnames'
import { useRouter } from 'next/router'

import {
  heroGap,
  marginHeader,
  sectionGap,
  sectionGapSmall,
  sectionPadding,
  sectionPaddingLarge,
} from 'project/padding'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import MailPortalHighlights, {
  virtualOfficeHighlights,
} from 'components/cards/new/common/MailPortalHighlights'
import Icon from 'components/Icon'
import { DesktopBreak } from 'components/DesktopBreak'
import Supademo from 'components/Supademo'
import { createClient } from '../prismicio'
import { PlanSlug } from 'common/planSlugs'
import { Constants, Format, Utils } from 'common/utils'
import WhyChoose from 'components/WhyChoose'
import { ButtonType } from '../types/global'
import FAQList from 'components/util/FAQList'
import { LocalBusinessJsonLd } from 'next-seo'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'
import { planPrice } from 'common/utils/planPrice'
import HeroBanner from 'components/hero/HeroBanner'
import { getAddons, getPlans } from 'common/planData'
import HeadingAndText from 'components/HeadingAndText'
import { getFAQList, getReviews } from 'common/cms-sdk'
import { seoStrings } from 'common/strings/seo-strings'
import CompareCards from 'components/cards/CompareCards'
import useWindowSize from 'components/util/useWindowSize'
import SetupVOin10 from 'components/cards/new/SetupVOin10'
import { I18NConfig } from 'next/dist/server/config-shared'
import ReviewsSection from 'components/sections/ReviewsSection'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SubscriptionPricing from 'components/SubscriptionPricing'
import SignUpIncludes from 'components/cards/new/SignUpIncludes'
import KeyBenefitsCard from 'components/cards/new/KeyBenefitsCard'
import LeftRobotCard from 'components/cards/new/common/LeftRobotCard'
import KeyBenefitsSection from 'components/sections/KeyBenefitsSection'
import PrivacyServiceCard from 'components/cards/new/PrivacyServiceCard'
import { TAddon, TFAQList, TPlanSummary, TReviews } from 'common/cms-types'
import { virtualOfficeStrings } from 'common/strings/virtual-office-strings'
import EmbededNewsletterForm from 'components/sections/EmbededNewsletterForm'
import PostroomReinventedCard from 'components/cards/new/PostroomReinventedCard'
import { virtualOfficeOldStreetBreadcrumb } from 'components/util/containers/Breadcrumbs'
import NoBoundariesLimitiationsSection from 'components/cards/new/NoBoundariesLimitiationsSection'

export type PlansPageType = {
  plans: TPlanSummary[]
  reviews: TReviews
  faqList: TFAQList
  addons: TAddon[]
}

const PlansPage: NextPageWithLayout<PlansPageType> = ({
  addons,
  faqList,
  plans,
  reviews,
}) => {
  const router = useRouter()
  const size = useWindowSize()

  const dailyPrice = planPrice.dailyOnYearlyPlan(PlanSlug.SCAN_LITE)
  const yearlyPrice = planPrice.yearly[PlanSlug.SCAN_LITE]

  const seoProps: SeoContainerProps['seoProps'] = {
    description: `Professional virtual office address in London from ${dailyPrice}p/day (${yearlyPrice}/year). Registered office, AI mail handling by HaiRo™, HMRC compliant. No setup fees.`,
    openGraph: {
      description: `Virtual office address in London from ${dailyPrice}p/day. Registered office, AI mail handling, HMRC compliant. Over 100,000 customers to date trust Hoxton Mix.`,
      url: 'https://www.hoxtonmix.com/virtual-office-london-old-street/',
    },
    title: `Virtual Office Address London | Registered Office from ${dailyPrice}p/Day`,
  }

  if (!plans?.length) {
    throw new Error(`Unexpected API result for plans. Plans: ${plans}.`)
  }

  return (
    <SeoContainer
      breadcrumb={virtualOfficeOldStreetBreadcrumb()}
      seoProps={seoProps}
    >
      <LocalBusinessJsonLd
        type={'LocalBusiness'}
        id={'07212205'}
        name={'Virtual Office London'}
        description={seoStrings.virtualOfficeLondonDescription}
        images={['https://www.hoxtonmix.com/organisation.png']}
        openingHours={{
          closes: '16:00',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
        }}
        geo={{
          latitude: '51.52580153636331',
          longitude: '-0.08374956236618057',
        }}
        rating={{
          bestRating: reviews.best,
          ratingCount: reviews.numberOfReviews,
          ratingValue: reviews.average,
          reviewCount: reviews.numberOfReviews,
          worstRating: reviews.worst,
        }}
        hasMap='https://maps.app.goo.gl/hRoKBCKFnr7Jsejq5'
        telephone={'+44 20 3475 3374'}
        url='https://www.hoxtonmix.com/virtual-office-london-old-street/'
        address={{
          addressCountry: 'GB',
          addressLocality: 'London',
          addressRegion: 'Greater London',
          postalCode: Constants.companyInformation.postal_code,
          streetAddress: Constants.companyInformation.address2,
        }}
      />
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column',
          sectionGap,
        )}
      >
        <div className={classNames('d-flex flex-column', heroGap)}>
          <HeroBanner
            logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
            robotImage={{
              alt: 'HaiRo™ robot mascot holding envelopes',
              dimensions: {
                height: 1066,
                width: 827,
              },
              src: '/images/new/robot-holding-mail.png',
            }}
            cta={
              <SubscriptionPricing
                buttonType={ButtonType.VIRTUAL_OFFICE_LONDON_PAGE_HERO}
                buttonAlign='start'
                hideRegister
                planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!}
              />
            }
            title='Virtual Office London'
            description={
              <div
                className={classNames('d-flex flex-column', sectionGapSmall)}
              >
                <h3 className='fs-fluid-24-32 fw-semibold'>
                  Get a professional virtual office address in London and let
                  our AI agent <span className='color-pink2'>HaiRo™</span>{' '}
                  organise, digitise, and deliver your post.{' '}
                </h3>
              </div>
            }
            subtitle={<span className='fs-fluid-24-48'></span>}
          />
          <div
            className={classNames('container d-flex flex-column', sectionGap)}
          >
            <div>
              <div className={classNames('d-flex flex-column', marginHeader)}>
                <h2
                  className={classNames(
                    'fs-fluid-36-80 lh-105 fw-semibold text-center whitespace-nowrap',
                  )}
                >
                  <span>Your Virtual Office Address in London with</span>
                  <DesktopBreak />
                  <span>
                    <span className='color-pink2'>everything included</span> in
                    one simple plan.
                  </span>
                </h2>
                <h3 className='fw-semibold text-muted mt-lg-3 mt-2 text-center fs-fluid-20-36'>
                  HaiRo™ will automatically take care of your mail and
                  documents.
                </h3>
              </div>

              <SignUpIncludes />
            </div>
            <div className={`d-none d-lg-flex flex-column ${sectionGapSmall}`}>
              <div className={classNames('d-flex flex-column', marginHeader)}>
                <div className={sectionPaddingLarge}>
                  <h2
                    id='demo'
                    className={classNames(
                      'fs-fluid-36-80 lh-105 fw-semibold text-center',
                    )}
                  >
                    Our <span className='color-pink2'>Customer Portal</span>.
                  </h2>
                  <h3 className='fw-semibold text-muted text-center fs-fluid-20-36'>
                    With the Customer Portal, you can manage every aspect of
                    your virtual office - seamlessly and efficiently.
                  </h3>
                </div>
                <Supademo showcaseId={'cmbrtzben02ml1q0iqll8zeff'} />
              </div>
              <div className='text-center'>
                <SubscriptionPricing
                  planSummary={
                    plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!
                  }
                  buttonType={ButtonType.VIRTUAL_OFFICE_LONDON_PORTAL_DEMO_CTA}
                />
              </div>
            </div>
            <LeftRobotCard
              image={{
                alt: 'Robot arm with mail zoomed',
                desktop: {
                  dimensions: {
                    height: 368,
                    width: 289,
                  },
                  src: '/images/new/robot-arm-mail-zoomed.png',
                },
                mobile: {
                  dimensions: {
                    height: 1122,
                    width: 696,
                  },
                  src: '/images/new/robot-arm-mail.png',
                },
              }}
              imageContainerWidth={size.isLgOrHigher ? '45%' : '100%'}
              imageStyle={{
                objectFit: 'cover',
                objectPosition: 'right',
                width: '100%',
              }}
            >
              <div className='d-flex flex-1 flex-column gap-4 px-4 pt-4 pb-5 pe-lg-8 pt-lg-17 pb-lg-14 mt-lg-0 mt-n16'>
                <h2 className='fw-semibold fs-fluid-36-80 lh-105'>
                  {' '}
                  An Affordable Virtual Office Package
                </h2>
                <span className='fw-semibold lh-132 fs-fluid-18-22'>
                  {' '}
                  <>
                    <div className='d-flex flex-column gap-3'>
                      <div>
                        You get a customer-facing virtual business address to
                        register with the HMRC and our AI-powered mail
                        management product HaiRo™, all in the same plan, with
                        only a 10-minute setup time.
                      </div>
                      <div>
                        <h3 className='h4 color-pink2 fw-semibold'>
                          No set-up fees
                        </h3>
                        <div>
                          We do not believe in charging set-up fees. All plans
                          have no hidden fees and no extra costs.
                        </div>
                      </div>
                      <div>
                        <h3 className='h4 color-pink2 fw-semibold'>
                          Cancel any time — flexibility
                        </h3>
                        <div>
                          We offer rolling monthly contracts. This means you are
                          never tied into a long contract and can cancel any
                          time.
                        </div>
                      </div>
                      <div>
                        <h3 className='h4 color-pink2 fw-semibold'>
                          Pay how you like
                        </h3>
                        <div>
                          From just{' '}
                          <span className='fw-bold'>
                            {planPrice.monthlyOnYearlyPlan(PlanSlug.SCAN_LITE)}
                          </span>{' '}
                          (when paid annually). Monthly plans offer flexibility;
                          yearly plans include a generous discount.
                        </div>
                      </div>
                    </div>
                  </>
                </span>
              </div>
            </LeftRobotCard>
            <div className={sectionPaddingLarge}>
              <HeadingAndText
                className={classNames('px-lg-0 px-2 ', marginHeader)}
                classesForTitle='fs-fluid-36-80 lh-105'
                subtitleSize='large'
                title={virtualOfficeStrings.howVirtualOfficePackagesWorkTitle}
                subtitle={
                  virtualOfficeStrings.howVirtualOfficePackagesWorkDescription
                }
              />
              <div
                className={classNames(
                  'color-muted lead-large text-semibold',
                  marginHeader,
                )}
                dangerouslySetInnerHTML={{
                  __html:
                    virtualOfficeStrings.howVirtualOfficePackagesWorkContent,
                }}
              />
              <div className='fs-fluid-24-32 fw-semibold'>
                We also offer some additional add-on services to enhance your
                virtual London business address.
              </div>
              <ul className='color-muted mt-md-10 mt-6 lead-large fw-semibold d-flex flex-column gap-8'>
                <li>
                  <span className='text-body fw-bold'>
                    WhatsApp Business Number:
                  </span>{' '}
                  Setting up your business line is quick and hassle-free - no
                  special technology needed. You&apos;ll get a dedicated
                  WhatsApp-compatible business number, so you can connect with
                  customers professionally without sharing your personal mobile.
                  This means you can answer messages from home or on the go,
                  maintaining a professional presence wherever you are.
                </li>
                <li>
                  <span className='text-body fw-bold'>
                    Director's Service Address:
                  </span>{' '}
                  Protect your home address by using our London address as your
                  director&apos;s service address, keeping your details private
                  while meeting Companies House requirements.
                </li>
              </ul>
              <div className='d-flex flex-column text-center mt-lg-30'>
                <SubscriptionPricing
                  planSummary={
                    plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!
                  }
                  buttonType={
                    ButtonType.VIRTUAL_OFFICE_LONDON_DIRECTORS_ADDRESS_CTA
                  }
                />
              </div>
            </div>
            <div>
              <KeyBenefitsCard />
              <KeyBenefitsSection />
            </div>
            <NoBoundariesLimitiationsSection plans={plans} />
            <PostroomReinventedCard />
            <MailPortalHighlights
              title={
                <>
                  Take Charge with the <br className='d-none d-lg-block' />{' '}
                  Customer Portal
                </>
              }
              subtitle={
                <span className='fs-fluid-18-36 fw-semibold color-grey9'>
                  With the Customer Portal, you can manage every aspect{' '}
                  <br className='d-none d-lg-block' /> of your virtual office -
                  seamlessly and efficiently.
                </span>
              }
            >
              <ul
                style={{
                  maxWidth: 776,
                }}
                className='list-unstyled'
              >
                {virtualOfficeHighlights.map((highlight, index) => (
                  <li key={index} className='d-flex mb-6'>
                    <Icon
                      width={20}
                      name='tick-circle'
                      className='me-2 flex-shrink-0 mt-2'
                    />
                    <div>
                      <span className='fw-semibold'>{highlight.title}</span> -{' '}
                      {highlight.content}
                    </div>
                  </li>
                ))}
              </ul>
              <span className='fw-bold ms-lg-10 fs-fluid-20-2x'>
                Your Virtual Office, Fully in Your Hands
              </span>
            </MailPortalHighlights>
            <div>
              <HeadingAndText
                className={classNames('px-lg-0 px-2 ', marginHeader)}
                classesForTitle='fs-fluid-36-80 lh-105'
                title={`London's Best <span className='text-gradient-purple'> Virtual Office? </span> <br className='d-none d-lg-block' /> Compare for yourself`}
                subtitle={
                  <span
                    className='d-block mx-auto text-center'
                    style={{
                      maxWidth: '1150px',
                    }}
                  >
                    With so many virtual office providers, finding the right one
                    can be overwhelming. Many charge hidden fees, lack essential
                    services, or make cancellation difficult. At The Hoxton Mix,
                    we do things differently. Our transparent, virtual office
                    London plan gives you complete control - with no hidden
                    costs or long-term commitment.'
                  </span>
                }
              />
              <CompareCards />
            </div>
            <PrivacyServiceCard addons={addons} />
            <div>
              <HeadingAndText
                classesForTitle='fs-fluid-36-80 lh-105'
                headingSize={2}
                title={virtualOfficeStrings.whyChooseUs}
                subtitle=''
              />
              <div className='d-flex justify-content-md-center mt-lg-10 mt-6 px-lg-6'>
                <div className='col-lg-9 col-12 pt-8'>
                  <WhyChoose />
                </div>
              </div>
            </div>
            <SetupVOin10
              planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!}
            />

            <FAQList
              title={faqList.data.title}
              subtitle={faqList.data.subtitle}
              slices={faqList.data.slices}
              expandAllButton
              expandAllButtonDesktopOnly
            />
            <EmbededNewsletterForm />
          </div>
        </div>
        <ReviewsSection data={reviews} pageUrl={router.pathname} />
      </main>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)

    const plans = await getPlans()
    const addons = await getAddons(true)
    const reviews = await getReviews()
    const faqList = await getFAQList(
      client,
      lang,
      'virtual-office-london-old-street',
    )

    return {
      props: {
        addons,
        faqList,
        plans,
        reviews,
      },
    }
  } catch (e) {
    console.error(e)
    return {
      props: {},
    }
  }
}

export default PlansPage
